import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Shield, Users, Eye, Lock, BookOpen, Info, Target, Zap, Crown } from "lucide-react";
import API from "../config";

export default function Home() {
  const navigate = useNavigate();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showJoinDialog, setShowJoinDialog] = useState(false);
  const [playerName, setPlayerName] = useState("");
  const [roomCode, setRoomCode] = useState("");
  const [password, setPassword] = useState("");
  const [requirePassword, setRequirePassword] = useState(false);
  const [requireApproval, setRequireApproval] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showRulesDialog, setShowRulesDialog] = useState(false);
  const [activeRooms, setActiveRooms] = useState([]);
  const [myRooms, setMyRooms] = useState([]);
  const [myRoomsInfo, setMyRoomsInfo] = useState([]);

  useEffect(() => {
    // Salas donde el jugador tiene un ID guardado
    try {
      const joinedRooms = Object.keys(localStorage)
        .filter(key => key.startsWith('room_'))
        .map(key => key.replace('room_', ''));
      setMyRooms(joinedRooms);

      // Lista de salas activas persistente
      const list = JSON.parse(localStorage.getItem('active_rooms_list') || '[]');
      // Filtrar salas que ya no tienen el ID en localStorage (por si acaso se limpió manualmente)
      const validRooms = list.filter(r => localStorage.getItem(`room_${r.code}`));
      setMyRoomsInfo(validRooms);
    } catch (e) {
      console.warn("Could not access localStorage", e);
    }

    fetchActiveRooms();
    const interval = setInterval(fetchActiveRooms, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchActiveRooms = async () => {
    try {
      const response = await axios.get(`${API}/rooms/list`);
      setActiveRooms(response.data.rooms || []);
    } catch (error) {
      console.error("Error fetching rooms:", error);
    }
  };

  const handleCreateRoom = async (forcedName = null) => {
    let name = (forcedName || playerName).trim();

    if (!name) {
      const savedName = localStorage.getItem('player_name');
      if (savedName) {
        name = savedName;
      } else {
        setShowCreateDialog(true);
        return;
      }
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/rooms/create`, {
        player_name: name
      });

      const { room_code, player_id } = response.data;
      try {
        localStorage.setItem(`room_${room_code}`, player_id);
        localStorage.setItem(`player_name`, name);
      } catch (e) {
        console.warn("Could not save to localStorage", e);
      }
      toast.success(`Sala creada: ${room_code}`);
      navigate(`/lobby/${room_code}`);
    } catch (error) {
      toast.error("Error al crear la sala");
      console.error(error);
    } finally {
      setLoading(false);
      setShowCreateDialog(false);
    }
  };

  const handleJoinRoom = async (directCode = null) => {
    let code = "";
    if (directCode) {
      code = String(directCode).replace(/\s/g, '').toUpperCase();
    } else {
      code = String(roomCode).replace(/\s/g, '').toUpperCase();
    }

    if (!code) {
      toast.error("Ingresa el código de sala");
      return;
    }

    let name = String(playerName || "").trim();
    if (!name) {
      const savedName = localStorage.getItem(`player_name`);
      if (savedName) {
        name = savedName;
      } else {
        if (directCode) {
          setRoomCode(code);
          setShowJoinDialog(true);
          return;
        }
        toast.error("Ingresa tu nombre");
        return;
      }
    }

    const savedPlayerId = localStorage.getItem(`room_${code}`);
    if (savedPlayerId) {
      try {
        await axios.post(`${API}/rooms/${code}/reconnect`, {
          player_id: savedPlayerId
        });
        navigate(`/lobby/${code}`);
        return;
      } catch (error) {
        localStorage.removeItem(`room_${code}`);
      }
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/rooms/join`, {
        room_code: code,
        player_name: name,
        password: password || null
      });

      const { player_id, room_code: joinedCode } = response.data;
      localStorage.setItem(`room_${joinedCode}`, player_id);
      localStorage.setItem(`player_name`, name);
      navigate(`/lobby/${joinedCode}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al unirse");
    } finally {
      setLoading(false);
      setShowJoinDialog(false);
    }
  };

  return (
    <div className="min-h-screen scanlines relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1603329469882-422fb52d50ea?crop=entropy&cs=srgb&fm=jpg&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'grayscale(100%)'
        }}
      />

      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 py-12">
        <div className="text-center space-y-6 max-w-4xl mb-12" data-testid="hero-section">
          <h1 className="font-display text-6xl md:text-7xl lg:text-8xl font-black tracking-tight leading-none">
            <span className="text-[hsl(var(--cyber-yellow))] drop-shadow-[0_0_20px_rgba(255,220,90,0.6)]">IMPOSTOR</span>
            {" "}
            <span className="text-[hsl(var(--text-primary))]]">GAMES</span>
          </h1>
          <p className="font-body text-lg md:text-xl text-[hsl(var(--text-secondary))] max-w-2xl mx-auto leading-relaxed">
            Un juego de engaño, deducción y secretos. ¿Eres un agente, un infiltrado, o el impostor que lo descubrirá todo?
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 mb-16" data-testid="cta-buttons">
          <Button
            data-testid="create-room-button"
            onClick={() => handleCreateRoom()}
            className="bg-[hsl(var(--cyber-yellow))] text-[hsl(240,10%,8%)] hover:bg-[hsl(var(--cyber-yellow))]/90 font-heading font-semibold text-lg px-8 py-6 rounded-lg shadow-[0_0_30px_rgba(255,220,90,0.4)] hover:shadow-[0_0_40px_rgba(255,220,90,0.6)] transition-all duration-200 hover:-translate-y-1"
            size="lg"
          >
            CREAR SALA
          </Button>
          <Button
            data-testid="join-room-button"
            onClick={() => setShowJoinDialog(true)}
            className="bg-[hsl(var(--charcoal))] text-[hsl(var(--text-primary))] border border-[hsl(var(--smoke))] hover:bg-[hsl(var(--smoke))] hover:border-white/20 font-heading font-semibold text-lg px-8 py-6 rounded-lg transition-all duration-200 hover:-translate-y-1"
            size="lg"
            variant="outline"
          >
            UNIRSE A SALA
          </Button>
          <Button
            data-testid="local-mode-button"
            onClick={() => navigate('/local-setup')}
            className="bg-[hsl(var(--electric-cyan))] text-[hsl(240,10%,8%)] hover:bg-[hsl(var(--electric-cyan))]/90 font-heading font-semibold text-lg px-8 py-6 rounded-lg shadow-[0_0_30px_rgba(0,255,255,0.4)] hover:shadow-[0_0_40px_rgba(0,255,255,0.6)] transition-all duration-200 hover:-translate-y-1"
            size="lg"
          >
            MODO LOCAL
          </Button>
          <Button
            data-testid="rules-button-main"
            onClick={() => setShowRulesDialog(true)}
            className="bg-[hsl(var(--charcoal))] text-[hsl(var(--text-primary))] border border-[hsl(var(--smoke))] hover:border-[hsl(var(--cyber-yellow))]/40 font-heading font-semibold text-lg px-8 py-6 rounded-lg transition-all duration-200 hover:-translate-y-1 hover:bg-white/5"
            size="lg"
            variant="ghost"
          >
            <BookOpen className="w-5 h-5 mr-2 text-[hsl(var(--cyber-yellow))]" />
            REGLAS
          </Button>
        </div>

        {myRoomsInfo.length > 0 && (
          <div className="relative z-10 max-w-5xl mx-auto w-full mb-8">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Zap className="w-5 h-5 text-[hsl(var(--hot-pink))]" />
              <h2 className="font-heading text-xl font-black text-[hsl(var(--hot-pink))] tracking-widest uppercase">
                MIS PARTIDAS RECIENTES
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {myRoomsInfo.map((room) => (
                <div
                  key={room.code}
                  className="glass border-[hsl(var(--hot-pink))]/30 rounded-xl p-4 hover:border-[hsl(var(--hot-pink))] transition-all cursor-pointer group relative overflow-hidden"
                  onClick={() => navigate(`/game/${room.code}`)}
                >
                  <div className="absolute top-0 right-0 p-2 opacity-10 group-hover:opacity-30 transition-opacity">
                    <Shield className="w-12 h-12 text-[hsl(var(--hot-pink))]" />
                  </div>
                  <div className="flex flex-col gap-2 relative z-10">
                    <div className="flex items-center justify-between">
                      <div className="text-2xl font-display font-black text-white tracking-tighter">
                        {room.code}
                      </div>
                      {room.isLocal !== undefined && (
                        <Badge variant="outline" className={`text-[9px] font-bold px-1.5 h-5 ${room.isLocal ? 'border-[hsl(var(--electric-cyan))] text-[hsl(var(--electric-cyan))]' : 'border-[hsl(var(--cyber-yellow))] text-[hsl(var(--cyber-yellow))]'}`}>
                          {room.isLocal ? 'LOCAL' : 'ONLINE'}
                        </Badge>
                      )}
                    </div>

                    <div className="flex items-center gap-3 text-[10px] font-black text-[hsl(var(--text-muted))] uppercase tracking-widest">
                      <span>{new Date(room.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                      {room.playerCount > 0 && (
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3" /> {room.playerCount}
                        </span>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="mt-2 w-full h-8 bg-[hsl(var(--hot-pink))]/10 text-[hsl(var(--hot-pink))] group-hover:bg-[hsl(var(--hot-pink))] group-hover:text-white transition-colors text-[10px] font-black"
                    >
                      REENTRAR PARTIDA
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeRooms.length > 0 && (
          <div className="relative z-10 max-w-5xl mx-auto w-full mb-12">
            <h2 className="font-heading text-xl font-bold text-center mb-4 text-[hsl(var(--cyber-yellow))]">
              SALAS ACTIVAS
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="active-rooms-list">
              {activeRooms.map((room) => {
                const isParticipant = myRooms.includes(room.code);
                return (
                  <div
                    key={room.code}
                    className="glass rounded-lg p-3 hover:-translate-y-1 transition-all duration-200 cursor-pointer"
                    onClick={() => handleJoinRoom(room.code)}
                    data-testid={`room-card-${room.code}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="font-heading text-lg text-[hsl(var(--cyber-yellow))]">
                          {room.code}
                        </div>
                        {isParticipant && (
                          <Badge variant="outline" className="text-[10px] border-[hsl(var(--electric-cyan))] text-[hsl(var(--electric-cyan))]">
                            PARTICIPANDO
                          </Badge>
                        )}
                      </div>
                      <Badge className={`${room.status === "playing"
                        ? "bg-[hsl(var(--hot-pink))]/20 text-[hsl(var(--hot-pink))] text-[10px]"
                        : "bg-[hsl(var(--laser-green))]/20 text-[hsl(var(--laser-green))] text-[10px]"
                        }`}>
                        {room.status === "playing" ? "JUGANDO" : "ESPERANDO"}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-[hsl(var(--text-secondary))]">
                      <Users className="w-3 h-3" />
                      <span>{room.player_count} jugadores</span>
                    </div>
                    {room.status === "playing" && room.current_round && (
                      <div className="text-[10px] text-[hsl(var(--text-muted))] mt-1">
                        Ronda {room.current_round}{room.max_rounds ? `/${room.max_rounds}` : ""}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Features section removed to declutter home screen */}
      </div>

      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="glass border-[hsl(var(--smoke))]" data-testid="create-room-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl">Crear Nueva Sala</DialogTitle>
            <DialogDescription className="text-[hsl(var(--text-secondary))]">
              Configura tu sala de juego
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="create-name" className="font-heading">TU NOMBRE</Label>
              <Input
                id="create-name"
                data-testid="create-player-name-input"
                placeholder="Agente 007"
                value={playerName}
                onChange={(e) => setPlayerName(e.target.value)}
                className="bg-[hsl(var(--charcoal))] border-[hsl(var(--smoke))] text-[hsl(var(--text-primary))]]"
              />
            </div>

            <Button
              data-testid="confirm-create-room-button"
              onClick={handleCreateRoom}
              disabled={loading || !playerName.trim()}
              className="w-full bg-[hsl(var(--cyber-yellow))] text-[hsl(240,10%,8%)] hover:bg-[hsl(var(--cyber-yellow))]/90 font-heading font-semibold disabled:opacity-50"
            >
              {loading ? "CREANDO..." : "CREAR SALA"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showJoinDialog} onOpenChange={setShowJoinDialog}>
        <DialogContent className="glass border-[hsl(var(--smoke))]" data-testid="join-room-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl">Unirse a Sala</DialogTitle>
            <DialogDescription className="text-[hsl(var(--text-secondary))]">
              Ingresa el código de sala y tu nombre
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="room-code" className="font-heading">CÓDIGO DE SALA</Label>
              <Input
                id="room-code"
                data-testid="room-code-input"
                placeholder="ABC123"
                value={roomCode}
                onChange={(e) => setRoomCode(e.target.value.replace(/\s/g, '').toUpperCase())}
                maxLength={6}
                className="bg-[hsl(var(--charcoal))] border-[hsl(var(--smoke))] text-[hsl(var(--text-primary))]] font-heading text-2xl text-center"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="join-name" className="font-heading">TU NOMBRE</Label>
              <Input
                id="join-name"
                data-testid="join-player-name-input"
                placeholder="Agente 007"
                value={playerName}
                onChange={(e) => setPlayerName(e.target.value)}
                className="bg-[hsl(var(--charcoal))] border-[hsl(var(--smoke))] text-[hsl(var(--text-primary))]]"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="join-password" className="font-heading flex items-center gap-2">
                <Lock className="w-4 h-4" />
                CONTRASEÑA (si tiene)
              </Label>
              <Input
                id="join-password"
                type="password"
                placeholder="Opcional"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-[hsl(var(--charcoal))] border-[hsl(var(--smoke))] text-[hsl(var(--text-primary))]]"
                data-testid="join-password-input"
              />
            </div>
            <Button
              data-testid="confirm-join-room-button"
              onClick={() => handleJoinRoom()}
              disabled={loading || !playerName.trim() || !roomCode.trim()}
              className="w-full bg-[hsl(var(--electric-cyan))] text-[hsl(240,10%,8%)] hover:bg-[hsl(var(--electric-cyan))]/90 font-heading font-semibold disabled:opacity-50"
            >
              {loading ? "UNIÉNDOSE..." : "UNIRSE"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showRulesDialog} onOpenChange={setShowRulesDialog}>
        <DialogContent className="glass border-[hsl(var(--cyber-yellow))] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-4xl text-[hsl(var(--cyber-yellow))] text-center">CÓMO JUGAR</DialogTitle>
            <DialogDescription className="text-center text-[hsl(var(--text-secondary))] text-base">
              Descubre quién miente, vota para eliminar de ronda y gana la partida.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <section className="space-y-3">
              <h4 className="font-heading font-bold text-[hsl(var(--cyber-yellow))] flex items-center gap-2">
                <Target className="w-5 h-5" /> EL OBJETIVO (3-20 Jugadores)
              </h4>
              <p className="text-sm leading-relaxed text-[hsl(var(--text-primary))]">
                Cada ronda tendréis una palabra secreta (excepto el Impostor).
                Deberéis describir vuestra palabra con <strong>una sola palabra</strong> por turno.
                Cada jugador debe usar la deducción para identificar quién NO tiene su palabra exacta y eliminarlo de ronda.
              </p>
            </section>

            <section className="space-y-4">
              <h4 className="font-heading font-bold text-[hsl(var(--cyber-yellow))] flex items-center gap-2">
                <Shield className="w-5 h-5" /> LOS ROLES
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="p-3 rounded-lg border border-[hsl(var(--electric-cyan))]/20 bg-[hsl(var(--electric-cyan))]/5">
                  <span className="block font-black text-[hsl(var(--electric-cyan))] mb-1">AGENTE</span>
                  <p className="text-[10px] leading-tight text-[hsl(var(--text-secondary))]">
                    Sabe la palabra secreta. Debe encontrar a los demás roles sin decir nada obvio.
                  </p>
                </div>
                <div className="p-3 rounded-lg border border-[hsl(var(--hot-pink))]/20 bg-[hsl(var(--hot-pink))]/5">
                  <span className="block font-black text-[hsl(var(--hot-pink))] mb-1">INFILTRADO</span>
                  <p className="text-[10px] leading-tight text-[hsl(var(--text-secondary))]">
                    Tiene una palabra parecida. Debe pasar desapercibido y detectar a los agentes.
                  </p>
                </div>
                <div className="p-3 rounded-lg border border-amber-500/20 bg-amber-500/5">
                  <span className="block font-black text-amber-500 mb-1">IMPOSTOR</span>
                  <p className="text-[10px] leading-tight text-[hsl(var(--text-secondary))]">
                    No sabe la palabra. Debe deducirla de las pistas de los demás para ganar al final.
                  </p>
                </div>
              </div>
            </section>

            <section className="space-y-3 bg-white/5 p-4 rounded-lg border border-white/10">
              <h4 className="font-heading font-bold text-[hsl(var(--cyber-yellow))] flex items-center gap-2">
                <BookOpen className="w-5 h-5" /> FLUJO DEL JUEGO
              </h4>
              <ul className="list-disc list-inside text-xs space-y-2 text-[hsl(var(--text-secondary))]">
                <li>Cada jugador dice una palabra por turno siguiendo el orden.</li>
                <li>Tras una ronda completa, se vota para eliminar a alguien de ronda.</li>
                <li><strong>Múltiples rondas</strong>: Se realizarán tantas votaciones como sea necesario hasta alcanzar una condición de victoria.</li>
                <li>Si se elimina de ronda al Impostor, tiene una oportunidad de adivinar la palabra de los Agentes para robar la victoria.</li>
                <li className="text-[hsl(var(--laser-green))] font-bold">Victoria (Agentes): Eliminar de ronda a todos los Infiltrados y al Impostor.</li>
                <li className="text-[hsl(var(--hot-pink))] font-bold">Victoria (Infiltrados/Impostor): Si solo quedan 2 jugadores y aún queda algún "enemigo" vivo.</li>
              </ul>
            </section>

            <section className="space-y-3 bg-purple-900/10 p-4 rounded-lg border border-purple-500/20">
              <h4 className="font-heading font-bold text-purple-400 flex items-center gap-2">
                <Crown className="w-5 h-5" /> MODO DIRECTOR (Local)
              </h4>
              <ul className="list-disc list-inside text-xs space-y-2 text-[hsl(var(--text-secondary))]">
                <li>Opción exclusiva para <strong>Partidas Locales</strong>.</li>
                <li>Un jugador actúa como <strong>Director</strong> y no participa como Agente, Infiltrado o Impostor.</li>
                <li>El Director <strong>escribe las palabras secretas</strong> manualmente para esa ronda.</li>
                <li>El rol de Director se elige al inicio de cada ronda (puede rotar).</li>
              </ul>
            </section>

            <section className="space-y-3">
              <h4 className="font-heading font-bold text-[hsl(var(--cyber-yellow))] flex items-center gap-2">
                <Zap className="w-5 h-5" /> PUNTUACIÓN
              </h4>
              <div className="flex justify-between gap-4">
                <div className="flex-1 p-2 rounded border border-white/5 text-center">
                  <span className="block text-xs font-bold text-[hsl(var(--electric-cyan))]">Agente</span>
                  <span className="text-lg font-black text-white">+1</span>
                </div>
                <div className="flex-1 p-2 rounded border border-white/5 text-center">
                  <span className="block text-xs font-bold text-[hsl(var(--hot-pink))]">Infiltrado</span>
                  <span className="text-lg font-black text-white">+2</span>
                </div>
                <div className="flex-1 p-2 rounded border border-white/5 text-center">
                  <span className="block text-xs font-bold text-amber-500">Impostor</span>
                  <span className="text-lg font-black text-white">+3</span>
                </div>
              </div>
            </section>
          </div>

          <Button
            onClick={() => setShowRulesDialog(false)}
            className="w-full bg-[hsl(var(--cyber-yellow))] text-black font-bold h-12"
          >
            ¡ENTENDIDO!
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}